import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_ROLE_COLORS, resolveSeriesColor } from '@/theme/chartPalette';
import { CHART_AXIS_TICK, DEFAULT_CHART_HEIGHT } from './chartStyles';

export interface MetricLinePoint extends Record<string, string | number | null> {
  date: string;
  value: number;
  detail: string | null;
}

interface MetricLineChartProps {
  points: MetricLinePoint[];
  minimumValue: number;
  maximumValue: number | null;
  accessibleLabel: string;
  detailLabel: string;
  formatValue: (value: number) => string;
  formatDate: (isoDate: string) => string;
  height?: number;
}

export function MetricLineChart({
  points,
  minimumValue,
  maximumValue,
  accessibleLabel,
  detailLabel,
  formatValue,
  formatDate,
  height = DEFAULT_CHART_HEIGHT,
}: MetricLineChartProps) {
  const lineColor = resolveSeriesColor(0);
  return (
    <div role="img" aria-label={accessibleLabel} className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={CHART_ROLE_COLORS.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={CHART_AXIS_TICK}
            axisLine={{ stroke: CHART_ROLE_COLORS.grid }}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            domain={[minimumValue, maximumValue === null ? 'auto' : maximumValue]}
            tickFormatter={formatValue}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          <Tooltip
            content={
              <MetricLineTooltip
                formatValue={formatValue}
                formatDate={formatDate}
                detailLabel={detailLabel}
              />
            }
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={lineColor}
            strokeWidth={2}
            dot={{ r: 3, fill: lineColor, strokeWidth: 0 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface MetricLineTooltipProps {
  formatValue: (value: number) => string;
  formatDate: (isoDate: string) => string;
  detailLabel: string;
  active?: boolean;
  payload?: Array<{ payload: MetricLinePoint }>;
}

function MetricLineTooltip({
  active,
  payload,
  formatValue,
  formatDate,
  detailLabel,
}: MetricLineTooltipProps) {
  if (active !== true || payload === undefined) {
    return null;
  }
  const firstEntry = payload[0];
  if (firstEntry === undefined) {
    return null;
  }
  const point = firstEntry.payload;
  return (
    <div className="rounded-lg bg-zooindigo px-3 py-2 text-xs text-zoowhite shadow-lg max-w-[220px]">
      <p className="font-bold">{formatDate(point.date)}</p>
      <p>{formatValue(point.value)}</p>
      {point.detail === null || point.detail.length === 0 ? null : (
        <p className="mt-1 text-zoopalelavender">
          {detailLabel}: {point.detail}
        </p>
      )}
    </div>
  );
}
